/**
 * SupporterWall — encouraging notes from donors + registered accounts.
 *
 * Sits directly under the Donor Wall (donation feed) on the campaign
 * detail page. Warm, personal, and gated:
 *
 *   Can post
 *   --------
 *     • A donor with a saved profile (localStorage `mra_donor_profile_v1`)
 *     • A donor who donated in the current session (parent flips
 *       `sessionDonated` via DonateDialog's onComplete)
 *   Cannot post
 *   -----------
 *     • Any visitor without either signal. Shown a warm CTA that offers
 *       to donate or (once real accounts exist) register.
 *
 * Content passes through `screenMessage()` (see lib/profanityFilter.js)
 * — messages containing prohibited terms are rejected with a toast and
 * are never posted.
 *
 * Display rules:
 *   • Donors who chose anonymity on their last gift → "Anonymous Donor"
 *   • Everyone else → first name + last initial (e.g. "Sarah T.")
 *
 * TODO(backend): everything ephemeral for now — see /app/HANDOFF.md.
 */

import { useEffect, useMemo, useState } from "react";
import { MessagesSquare, Send, ShieldAlert, UserPlus, Flag, Check, X } from "lucide-react";
import { HeartIcon } from "../lib/icons";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { loadProfile } from "../lib/donorProfile";
import { screenMessage } from "../lib/profanityFilter";
import { getSeededMessages, dotFor } from "../data/supporterWall";
import {
  reportMessage,
  isRemoved,
  isFlagged,
  subscribeReports,
  REPORT_REASONS,
} from "../lib/wallReports";

// Duplicate of the DonateDialog prefs key — kept read-only here so we can
// tell whether the last donation was anonymous ("showName: false").
const PREFS_KEY = "mra_donor_prefs_v1";
const readPrefs = () => {
  if (typeof window === "undefined") return null;
  try { return JSON.parse(window.localStorage.getItem(PREFS_KEY) || "null"); } catch (e) { return null; }
};

const MAX_LEN = 320;
const money = (n) => `$${Number(n).toLocaleString()}`;

// "Sarah Thomas" -> "Sarah T."
const displayNameFromProfile = (profile) => {
  const parts = (profile?.fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "You";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const dotColorCls = {
  tangerine: "bg-tangerine",
  cobalt:    "bg-cobalt",
  grass:     "bg-grass",
  blossom:   "bg-blossom",
  yellow:    "bg-yellow",
};

// Cheap "3 days ago" formatter for the prototype.
const relTime = (iso) => {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const SupporterWall = ({
  campaignId,
  studentFirstName,
  hasDonatedThisSession = false,
  onOpenDonate,
}) => {
  const profile = loadProfile();
  const prefs = readPrefs();
  const canPost = hasDonatedThisSession || !!profile;
  const isAnonymousDonor = prefs?.showName === false;

  // Session-scoped message list. TODO(backend): fetch from API.
  const [messages, setMessages] = useState(() => getSeededMessages());
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  // Which message currently has its Report popover open (null = none).
  const [reportOpenId, setReportOpenId] = useState(null);
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0].key);
  // Force-refresh signal so the list re-filters when admins remove messages.
  const [reportsTick, setReportsTick] = useState(0);

  useEffect(() => {
    const unsub = subscribeReports(() => setReportsTick((t) => t + 1));
    return unsub;
  }, []);

  const authorPreview = useMemo(() => {
    if (isAnonymousDonor) return "Anonymous Donor";
    return displayNameFromProfile(profile);
  }, [profile, isAnonymousDonor]);

  const remaining = MAX_LEN - draft.length;
  const overLimit = remaining < 0;

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      toast.error("Add a few words before posting.");
      return;
    }
    if (overLimit) {
      toast.error(`Please shorten your message by ${Math.abs(remaining)} characters.`);
      return;
    }
    const hits = screenMessage(trimmed);
    if (hits.length > 0) {
      toast.error("That message has language we can't post.", {
        description: "Please revise it and try again.",
      });
      return;
    }

    // TODO(backend): POST /api/campaigns/:id/wall { message } — server owns
    // the canonical profanity screen and returns the persisted record.
    setPosting(true);
    setTimeout(() => {
      const newMsg = {
        id: `local-${Date.now()}`,
        authorName: authorPreview,
        message: trimmed,
        postedAt: new Date().toISOString(),
        color: dotFor(messages.length),
        justAdded: true,
      };
      setMessages((prev) => [newMsg, ...prev]);
      setDraft("");
      setPosting(false);
      toast.success("Message posted", { description: `${studentFirstName} will see this on their campaign.` });
    }, 450);
  };

  // Filter out any message an admin has hard-removed. `reportsTick` is
  // referenced to trigger a re-render whenever the reports store changes.
  const visibleMessages = useMemo(() => {
    void reportsTick;
    return messages.filter((m) => !isRemoved(m.id));
  }, [messages, reportsTick]);

  const submitReport = (msg) => {
    reportMessage(
      {
        id: msg.id,
        campaignId,
        studentFirstName,
        authorName: msg.authorName,
        message: msg.message,
        postedAt: msg.postedAt,
      },
      reportReason,
    );
    toast.success("Thanks. We'll take a look.", {
      description: "The message stays visible while our team reviews it.",
    });
    setReportOpenId(null);
    setReportReason(REPORT_REASONS[0].key);
  };

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-border bg-bone p-6 lg:p-7"
      data-testid="supporter-wall"
    >
      {/* Ambient palette wash — matches the donor feed's warm treatment. */}
      <span className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blossom/25 blur-3xl" aria-hidden />
      <span className="pointer-events-none absolute -left-14 -bottom-14 h-36 w-36 rounded-full bg-cobalt/12 blur-3xl" aria-hidden />

      {/* Header */}
      <div className="relative flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cobalt/12">
          <MessagesSquare className="h-4.5 w-4.5 text-cobalt" strokeWidth={1.75} />
        </span>
        <div>
          <h3 className="font-serif text-3xl leading-none text-ink">Supporter wall</h3>
          <p className="text-[13px] text-olive">Notes of encouragement for {studentFirstName}.</p>
        </div>
      </div>

      {/* Composer OR sign-up prompt */}
      <div className="relative mt-5">
        {canPost ? (
          <div className="rounded-lg border border-border/60 bg-sand/60 p-4" data-testid="supporter-composer">
            <label htmlFor={`wall-input-${campaignId}`} className="sr-only">Leave an encouraging message</label>
            <textarea
              id={`wall-input-${campaignId}`}
              data-testid="supporter-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              placeholder={`Leave a note for ${studentFirstName}…`}
              className="block w-full resize-none bg-transparent text-base leading-relaxed text-ink placeholder:text-olive/60 focus:outline-none"
              maxLength={MAX_LEN + 40}
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[13px] text-olive">
                Posting as <span className="font-semibold text-ink">{authorPreview}</span>
              </p>
              <div className="flex items-center gap-3">
                <span
                  className={`text-[13px] tabular-nums ${overLimit ? "font-semibold text-scarlet" : "text-olive/70"}`}
                  data-testid="supporter-counter"
                >
                  {remaining}
                </span>
                <button
                  type="button"
                  onClick={submit}
                  disabled={posting || !draft.trim() || overLimit}
                  data-testid="supporter-submit"
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold uppercase tracking-wide text-sand transition hover:bg-ink/90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Send className="h-3 w-3" strokeWidth={2} />
                  {posting ? "Posting…" : "Post message"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-tangerine/40 bg-tangerine/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between"
            data-testid="supporter-locked"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tangerine/15">
                <ShieldAlert className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-[13px] font-semibold text-ink">
                  This wall is just for supporters.
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-olive">
                  Donors and registered supporters can leave a note.
                  Make a gift to unlock the wall, or sign up for a free account.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onOpenDonate}
                data-testid="supporter-cta-donate"
                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold uppercase tracking-wide text-sand transition hover:bg-ink/90"
              >
                <HeartIcon className="h-3 w-3" />
                Donate
              </button>
              <button
                type="button"
                data-testid="supporter-cta-signup"
                onClick={() =>
                  toast("Accounts coming soon", {
                    description: "For now, make a gift to unlock the wall.",
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-full bg-sand px-4 py-2 text-[13px] font-semibold uppercase tracking-wide text-ink ring-1 ring-inset ring-ink/15 transition hover:ring-ink/40"
              >
                <UserPlus className="h-3 w-3" strokeWidth={2} />
                Sign up
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Wall — chronological, newest first */}
      <ul className="relative mt-5 space-y-3" data-testid="supporter-list">
        <AnimatePresence initial={false}>
          {visibleMessages.map((m) => {
            const flagged = isFlagged(m.id);
            const isReportingThis = reportOpenId === m.id;
            return (
              <motion.li
                key={m.id}
                layout
                initial={m.justAdded ? { opacity: 0, y: -6 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className={`group flex items-start gap-3 rounded-lg border p-4 transition ${
                  flagged
                    ? "border-tangerine/30 bg-tangerine/[0.04]"
                    : "border-border/60 bg-sand/50"
                }`}
                data-testid="supporter-message"
              >
                <span
                  aria-hidden
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotColorCls[m.color] || "bg-tangerine"}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-serif text-base italic text-ink">{m.authorName}</span>
                    <span className="text-[13px] uppercase tracking-wide text-olive/70">
                      {relTime(m.postedAt)}
                    </span>
                    {flagged && (
                      <span
                        className="rounded-full bg-tangerine/15 px-2 py-0.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-tangerine"
                        data-testid="supporter-flag-chip"
                      >
                        Under review
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-base leading-relaxed text-ink/85">
                    {m.message}
                  </p>

                  {/* Inline report popover — expands under the message */}
                  {isReportingThis && (
                    <div
                      className="mt-3 rounded-md border border-ink/10 bg-bone p-3"
                      data-testid="supporter-report-form"
                    >
                      <p className="text-[13px] font-semibold text-ink">Why are you reporting this?</p>
                      <div className="mt-2 space-y-1.5">
                        {REPORT_REASONS.map((r) => (
                          <label key={r.key} className="flex items-center gap-2 text-[13px] text-ink">
                            <input
                              type="radio"
                              name={`report-${m.id}`}
                              value={r.key}
                              checked={reportReason === r.key}
                              onChange={() => setReportReason(r.key)}
                              className="h-3.5 w-3.5 border-ink/30 text-ink focus:ring-ink/30"
                              data-testid={`report-reason-${r.key}`}
                            />
                            {r.label}
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => { setReportOpenId(null); setReportReason(REPORT_REASONS[0].key); }}
                          className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[13px] font-medium text-ink/70 hover:bg-ink/5"
                          data-testid="report-cancel"
                        >
                          <X className="h-3 w-3" strokeWidth={2} /> Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => submitReport(m)}
                          className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1 text-[13px] font-semibold uppercase tracking-wide text-sand hover:bg-ink/90"
                          data-testid="report-submit"
                        >
                          <Check className="h-3 w-3" strokeWidth={2} /> Send report
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Report action — small, tucked to the right, revealed on hover */}
                {!isReportingThis && (
                  <button
                    type="button"
                    onClick={() => setReportOpenId(m.id)}
                    aria-label="Report this message"
                    title="Report this message"
                    data-testid={`supporter-report-${m.id}`}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 sm:opacity-0 rounded-full p-1.5 text-ink/40 transition hover:bg-ink/5 hover:text-ink"
                  >
                    <Flag className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                )}
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </div>
  );
};
